import type { ReactElement } from 'react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import type { IconProps } from 'components/icon/icon';
import Icon from 'components/icon/icon';
import Typography from 'components/typography/typography';
import type { IconTypes } from 'components/icon';

interface SideButtonProps {
  title?: string;
  onClick?: () => void;
  iconProps?: IconProps;
  disabled?: boolean;
  className?: string;
  titleClassName?: string;
}

interface TextButtonProps {
  previous?: SideButtonProps;
  next?: SideButtonProps;
  className?: string;
  titleClassName?: string;
  children?: ReactElement;
}

const TextButton: React.FC<TextButtonProps> = (props) => {
  const { previous, next, className } = props;

  const buttonClassName = twMerge('flex flex-row items-center');

  const textButtonClassNames = twMerge(
    'h-6',
    'flex flex-row items-center text-center gap-x-4 p-0',
    'text-gray600',
    className,
  );

  const hoverClassName = twMerge('hover:text-gray500 hover:underline');
  const activeClassName = twMerge('active:text-gray600 active:underline');

  const previousButtonClassNames = twMerge(
    previous?.disabled && 'text-gray500 ',
    buttonClassName,
    !previous?.disabled && hoverClassName,
    !previous?.disabled && activeClassName,
    previous?.className,
  );
  const previousIconProps = {
    icon: 'caret-left' as IconTypes,
    ...next?.iconProps,
  };

  const nextButtonClassNames = twMerge(
    next?.disabled && 'text-gray500',
    buttonClassName,
    !next?.disabled && hoverClassName,
    !next?.disabled && activeClassName,
    next?.className,
  );
  const nextIconProps = {
    icon: 'caret-right' as IconTypes,
    ...next?.iconProps,
  };

  return (
    <div className={textButtonClassNames}>
      {previous && (
        <button
          name="previous"
          type="button"
          className={previousButtonClassNames}
          onClick={previous?.onClick}
          disabled={previous?.disabled}
        >
          <Icon {...previousIconProps} />
          <Typography
            variant="body-large"
            as="p"
            className={previous.titleClassName}
          >
            {previous?.title}
          </Typography>
        </button>
      )}
      {props.children}
      {next && (
        <button
          name="next"
          type="button"
          className={nextButtonClassNames}
          onClick={next?.onClick}
          disabled={next?.disabled}
        >
          <Typography
            variant="body-large"
            as="p"
            className={next.titleClassName}
          >
            {next?.title}
          </Typography>
          <Icon {...nextIconProps} />
        </button>
      )}
    </div>
  );
};

export default TextButton;
